import type { CoreStores } from '@tinycld/core/lib/pocketbase'
import type { Schema } from '@tinycld/core/types/pbSchema'
import type { createCollection, SchemaDeclaration } from 'pbtsdb/core'
import type PocketBase from 'pocketbase'
import type { ComponentType, LazyExoticComponent, ReactNode } from 'react'
import type { AutomationDefinitions } from '../automation/types'
import type { PackageManifest } from './types'

// --- Spike-2-proven helpers (see ~/code/tinycld/new/spike2/sim.ts) ----------
export type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (
    k: infer I
) => void
    ? I
    : never

interface PackageSidebarProps {
    isCollapsed: boolean
}
export interface PackageProviderProps {
    children: ReactNode
}
/**
 * A package's app-wide provider, as a module loader rather than a component.
 *
 * Dynamic on purpose: a static import would pull the provider's module graph
 * (and through it, screens) into the collections module's eager import graph,
 * which used to form a require cycle. Not `lazy()` either: the workspace needs
 * every provider on every signed-in boot, and a lazy component suspends the
 * whole package area on its first render — long enough for the root navigator
 * to mount without it and write the bare app root over a deep link. Core
 * resolves the loaders before the route tree mounts instead
 * (lib/packages/provider-loader.ts).
 */
export interface PackageProviderLoader {
    load: () => Promise<{ default: ComponentType<PackageProviderProps> }>
}
export interface PackageSettingsPanel {
    slug: string
    label: string
    Component: ComponentType | LazyExoticComponent<ComponentType>
}
// System-wide settings panel (the /admin Settings section). Same shape as a
// per-org settings panel but rendered system-scoped, not per org.
export interface PackageSystemSettingsPanel {
    slug: string
    label: string
    Component: ComponentType | LazyExoticComponent<ComponentType>
    /** System-settings namespace this panel edits; hidden where it is managed. */
    keyPrefix?: string
}
export interface SidebarContribution {
    target: string
    slot: string
    order: number
    /** Shown where the host lays contributions out as tabs. */
    label?: string
    Component: ComponentType | LazyExoticComponent<ComponentType>
}
// A read-only event feed contributed to another package's event grid. `load`
// is a bare thunk (not React.lazy) because the module exports a HOOK
// (`useEventSource`) — see core/lib/event-sources/types.ts for the contract.
export interface PackageEventSource {
    target: string
    id: string
    label: string
    color?: string
    order: number
    load: () => Promise<unknown>
}
// A first-run wizard step. `load` is a bare thunk (not React.lazy) because
// the module exports hooks as well as the component — see
// core/lib/setup/types.ts for the contract.
export interface PackageSetupStep {
    id: string
    label: string
    order: string | null
    load: () => Promise<unknown>
}
export interface SeedUser {
    id: string
    username: string
    email: string
    name: string
}

export interface SeedContext {
    // Single-org: package seeds own data by the user id directly (the former
    // org/userOrg junction is gone; the `role` enum lives on the user record).
    // `username` is what mail derives mailbox addresses from — the server
    // provisions from username, never the email local-part, so seeds must too
    // or seeded users get a different address than the server would create.
    user: SeedUser

    // The seeded collaborator, for fixtures that need a second person: a
    // teammate-owned board, a shared calendar, a share recipient.
    //
    // Seeds MUST resolve "the other person" to this user rather than querying
    // `users` for `id != user.id`. That query returns real accounts, and a seed
    // that writes rows owned by them (a mailbox, a membership, a share) creates
    // data no user-scoped reset can ever reclaim — the nightly demo reset wipes
    // the demo user, and the litter attributed to everyone else accumulates
    // forever. Scoping every seeded row to {user, companion} is what makes the
    // reset's blast radius equal to what the seed actually created.
    //
    // Optional: a deployment seeded without a collaborator (or a package seeded
    // standalone) still works — seeds fall back to skipping the multi-user
    // fixtures rather than reaching for a stranger.
    companion?: SeedUser
}

// pbtsdb's collection factory, typed over the broadest schema. Each package's
// registerCollections declares its OWN narrower factory type (over
// `Schema & PkgSchema`); we accept the broad form here so any concrete package
// factory is assignable through the entry without fighting pbtsdb's invariant
// generic. This is sound because:
//   (1) the runtime call passes the real full-MergedSchema factory, and
//   (2) the type fidelity we depend on (useStore) is on the RETURN side (R),
//       which stays precisely inferred per entry — NOT this parameter.
//   (3) each package's registerCollections is already type-checked in its own
//       file against its own MergedSchema.
type AnyNewCollectionFn = ReturnType<typeof createCollection<SchemaDeclaration>>

// One entry in tinycld.config.ts. S = this package's schema type; R = its
// registerCollections return type. Both are captured for the derivations below.
// registerCollections is OPTIONAL — settings-only packages (e.g.
// google-takeout-import) contribute no collections; a missing one contributes
// {} to MergedSchema/packageStores. For those, pass S = Record<string, never>.
export interface PackageEntry<S extends SchemaDeclaration, R> {
    manifest: PackageManifest & { packageName?: string }
    registerCollections?: (newCollection: AnyNewCollectionFn, coreStores: CoreStores) => R
    schema?: S // covariant phantom carrier (plain S — see spike2/FINDINGS.md)
    sidebar?:
        | ComponentType<PackageSidebarProps>
        | LazyExoticComponent<ComponentType<PackageSidebarProps>>
        | null
    provider?: PackageProviderLoader | null
    settings?: PackageSettingsPanel[]
    systemSettings?: PackageSystemSettingsPanel[]
    sidebarContributions?: SidebarContribution[]
    search?: {
        label?: string
        load: () => Promise<unknown>
    }
    eventSources?: PackageEventSource[]
    setupSteps?: PackageSetupStep[]
    automation?: AutomationDefinitions
    seed?: (pb: PocketBase, ctx: SeedContext) => Promise<void>
}

// Entry constructor: schema type S given explicitly, register return R inferred.
// (R defaults to Record<string, never> when registerCollections is omitted.)
//
// The accepted `registerCollections` types its `newCollection` param as the
// caller's OWN factory (`Reg` — each package declares
// `ReturnType<typeof createCollection<Schema & PkgSchema>>`). We DON'T constrain
// that param to a single factory type, because pbtsdb's factory generic is
// invariant and a narrow per-package factory won't unify with a shared one
// (TS2322). We only care about inferring the RETURN type R (which feeds
// MergeSchemas/useStore). The stored entry widens the param to AnyNewCollectionFn.
export function definePackageEntry<S extends SchemaDeclaration>() {
    return <R = Record<string, never>, Reg = AnyNewCollectionFn>(entry: {
        manifest: PackageEntry<S, R>['manifest']
        registerCollections?: (newCollection: Reg, coreStores: CoreStores) => R
        sidebar?: PackageEntry<S, R>['sidebar']
        provider?: PackageEntry<S, R>['provider']
        settings?: PackageEntry<S, R>['settings']
        systemSettings?: PackageEntry<S, R>['systemSettings']
        sidebarContributions?: PackageEntry<S, R>['sidebarContributions']
        search?: PackageEntry<S, R>['search']
        eventSources?: PackageEntry<S, R>['eventSources']
        setupSteps?: PackageEntry<S, R>['setupSteps']
        automation?: PackageEntry<S, R>['automation']
        seed?: PackageEntry<S, R>['seed']
    }): PackageEntry<S, R> => entry as unknown as PackageEntry<S, R>
}

// --- derivations over a config array (the as-const tuple) --------------------
// Extract STRUCTURALLY (via the `schema` phantom property and the register
// RETURN type), NOT by matching `PackageEntry<...>`. Matching the full
// PackageEntry would force TS to resolve registerCollections' `coreStores:
// CoreStores` param — and CoreStores = typeof coreStores in pocketbase.ts,
// which is built from createCollection<MergeSchemas<typeof tinycldConfig>>.
// That round-trip is a circular type reference (TS2456/TS2502). Pulling only
// the `schema` carrier and the return type breaks the cycle: neither touches
// the param types.
export type SchemaOf<E> = E extends { schema?: infer S }
    ? S extends SchemaDeclaration
        ? S
        : Record<string, never>
    : Record<string, never>
export type RegisterReturnOf<E> = E extends {
    registerCollections?: (...args: never[]) => infer R
}
    ? R
    : Record<string, never>

export type MergeSchemas<Entries extends readonly unknown[]> = Schema &
    UnionToIntersection<SchemaOf<Entries[number]>>
// Empty-tuple guard: a lean shell (zero feature packages) has
// `tinycldConfig = [] as const` (type `readonly []`). Without this branch,
// `(readonly [])[number]` is `never`, `RegisterReturnOf<never>` is `never`, and
// `UnionToIntersection<never>` collapses to `unknown` — which is NOT spreadable,
// so `{ ...buildPackageStores(...) }` in pocketbase.ts fails to typecheck. The
// empty config contributes no stores, so the correct result is an empty (but
// spreadable) object type.
export type PackageStoresReturn<Entries extends readonly unknown[]> = Entries extends readonly []
    ? Record<string, never>
    : UnionToIntersection<RegisterReturnOf<Entries[number]>>
